// Validation utilities for Supplier Due Diligence forms

export interface ValidationError {
  field: string;
  message: string;
}

export interface BasicDetailsData {
  estimatedContractAmount: string;
  requestDate: string;
  requestorName: string;
  requestorManager: string;
  companyName: string;
  streetAddress: string;
  addressLine2: string; // Optional
  city: string;
  zipCode: string;
  state: string;
  country: string;
  phone: string;
  contactPerson: string;
  contactEmail: string;
  vendorWebsite: string;
  howLearnedAbout: string;
  goodsServicesDescription: string;
  competitiveBidsObtained: string;
}

export interface ThirdPartyRiskData {
  ofacSanctions: string;
  ofacSanctionsDetails: string;
  angelRelationships: string;
  angelRelationshipsDetails: string;
  personalInfoAccess: string;
  personalInfoDetails: string;
  specialCircumstances: string;
  specialCircumstancesDetails: string;
}

export interface CommercialDueDiligenceData {
  reputationalConcerns: string;
  reputationalConcernsDetails: string;
  researchDate: string;
  searchTermsTools: string;
  experienceCredentials: string;
  canProvideReferences: string;
  references: string;
  hasRequiredLicenses: string;
  licenseExplanation: string;
}

export interface BidsData {
  bid1Description: string;
  bid1Total: string;
  bid2Description: string;
  bid2Total: string;
  bid3Description: string;
  bid3Total: string;
  isLowestBid: string;
  selectionReason: string;
}

/**
 * Validate Form A: Basic Details
 * Everything is required except addressLine2
 */
export function validateBasicDetails(data: BasicDetailsData): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!data.estimatedContractAmount?.trim()) {
    errors.push({ field: 'estimatedContractAmount', message: 'Estimated Total Contract Value is required' });
  }
  if (!data.requestDate?.trim()) {
    errors.push({ field: 'requestDate', message: 'Request Date is required' });
  }
  if (!data.requestorName?.trim()) {
    errors.push({ field: 'requestorName', message: 'Requestor Name is required' });
  }
  if (!data.requestorManager?.trim()) {
    errors.push({ field: 'requestorManager', message: 'Approving Manager is required' });
  }
  if (!data.companyName?.trim()) {
    errors.push({ field: 'companyName', message: 'Company Name is required' });
  }
  if (!data.streetAddress?.trim()) {
    errors.push({ field: 'streetAddress', message: 'Street Address is required' });
  }
  // addressLine2 is optional
  if (!data.city?.trim()) {
    errors.push({ field: 'city', message: 'City is required' });
  }
  if (!data.zipCode?.trim()) {
    errors.push({ field: 'zipCode', message: 'Zip Code is required' });
  }
  if (!data.state?.trim()) {
    errors.push({ field: 'state', message: 'State/Province is required' });
  }
  if (!data.country?.trim()) {
    errors.push({ field: 'country', message: 'Country is required' });
  }
  if (!data.phone?.trim()) {
    errors.push({ field: 'phone', message: 'Phone is required' });
  }
  if (!data.contactPerson?.trim()) {
    errors.push({ field: 'contactPerson', message: 'Contact Person is required' });
  }
  if (!data.contactEmail?.trim()) {
    errors.push({ field: 'contactEmail', message: 'Contact Email is required' });
  }
  if (!data.vendorWebsite?.trim()) {
    errors.push({ field: 'vendorWebsite', message: 'Vendor Website is required' });
  }
  if (!data.howLearnedAbout?.trim()) {
    errors.push({ field: 'howLearnedAbout', message: 'How did you learn about this vendor is required' });
  }
  if (!data.goodsServicesDescription?.trim()) {
    errors.push({ field: 'goodsServicesDescription', message: 'Description of goods/services is required' });
  }
  if (!data.competitiveBidsObtained?.trim()) {
    errors.push({ field: 'competitiveBidsObtained', message: 'Competitive bids question is required' });
  }

  return errors;
}

/**
 * Validate Form B: Third Party Risk Profile Checklist
 * All radio buttons required
 * If any radio is YES, require corresponding details field
 */
export function validateThirdPartyRisk(data: ThirdPartyRiskData): ValidationError[] {
  const errors: ValidationError[] = [];

  // Question 1: OFAC Sanctions
  if (!data.ofacSanctions?.trim()) {
    errors.push({ field: 'ofacSanctions', message: 'OFAC Sanctions question is required' });
  } else if (data.ofacSanctions === 'YES' && !data.ofacSanctionsDetails?.trim()) {
    errors.push({ field: 'ofacSanctionsDetails', message: 'OFAC Sanctions details are required when answer is YES' });
  }

  // Question 2: Angel Relationships
  if (!data.angelRelationships?.trim()) {
    errors.push({ field: 'angelRelationships', message: 'Angel relationships question is required' });
  } else if (data.angelRelationships === 'YES' && !data.angelRelationshipsDetails?.trim()) {
    errors.push({ field: 'angelRelationshipsDetails', message: 'Relationship details are required when answer is YES' });
  }

  // Question 3: Personal Info Access
  if (!data.personalInfoAccess?.trim()) {
    errors.push({ field: 'personalInfoAccess', message: 'Personal information access question is required' });
  } else if (data.personalInfoAccess === 'YES' && !data.personalInfoDetails?.trim()) {
    errors.push({ field: 'personalInfoDetails', message: 'Personal information details are required when answer is YES' });
  }

  // Question 4: Special Circumstances
  if (!data.specialCircumstances?.trim()) {
    errors.push({ field: 'specialCircumstances', message: 'Special circumstances question is required' });
  } else if (data.specialCircumstances === 'YES' && !data.specialCircumstancesDetails?.trim()) {
    errors.push({ field: 'specialCircumstancesDetails', message: 'Special circumstances details are required when answer is YES' });
  }

  return errors;
}

/**
 * Validate Form C: Commercial Due Diligence
 */
export function validateCommercialDueDiligence(
  data: CommercialDueDiligenceData,
  estimatedContractAmount: string
): ValidationError[] {
  const errors: ValidationError[] = [];

  // Question 1a: Reputational Concerns
  if (!data.reputationalConcerns?.trim()) {
    errors.push({ field: 'reputationalConcerns', message: 'Reputational concerns question is required' });
  } else if (data.reputationalConcerns === 'YES' && !data.reputationalConcernsDetails?.trim()) {
    errors.push({ field: 'reputationalConcernsDetails', message: 'Details are required when reputational concerns exist' });
  }

  // Question 1b: Research Date (always required)
  if (!data.researchDate?.trim()) {
    errors.push({ field: 'researchDate', message: 'Research date is required' });
  }

  // Search terms/tools (required if 1a is YES, but based on form structure, seems always required)
  if (data.reputationalConcerns === 'YES' && !data.searchTermsTools?.trim()) {
    errors.push({ field: 'searchTermsTools', message: 'Search terms and tools are required' });
  }

  // Question 2a: Experience/Credentials (always required)
  if (!data.experienceCredentials?.trim()) {
    errors.push({ field: 'experienceCredentials', message: 'Experience and credentials explanation is required' });
  }

  // Question 2b: Both radio and text box required
  if (!data.canProvideReferences?.trim()) {
    errors.push({ field: 'canProvideReferences', message: 'References question is required' });
  }
  if (!data.references?.trim()) {
    errors.push({ field: 'references', message: 'References or explanation is required' });
  }

  // Question 3: Required if contract > $25,000
  const amount = parseFloat(estimatedContractAmount?.replace(/[^0-9.]/g, '') || '0');
  if (amount > 25000) {
    if (!data.hasRequiredLicenses?.trim()) {
      errors.push({ field: 'hasRequiredLicenses', message: 'Required licenses question is required for contracts over $25,000' });
    } else if (data.hasRequiredLicenses === 'NO' && !data.licenseExplanation?.trim()) {
      errors.push({ field: 'licenseExplanation', message: 'Explanation is required when licenses are not available' });
    }
  }

  return errors;
}

/**
 * Validate Form D: Competitive Bidding
 * Logic depends on Question 5 from Form A (competitiveBidsObtained)
 */
export function validateBids(data: BidsData, competitiveBidsObtained: string): ValidationError[] {
  const errors: ValidationError[] = [];

  if (competitiveBidsObtained === 'YES') {
    // Require Bid 1 and Bid 2 (Bid 3 is optional)
    if (!data.bid1Description?.trim()) {
      errors.push({ field: 'bid1Description', message: 'Bid 1 description is required when competitive bids were obtained' });
    }
    if (!data.bid1Total?.trim()) {
      errors.push({ field: 'bid1Total', message: 'Bid 1 total is required when competitive bids were obtained' });
    }
    if (!data.bid2Description?.trim()) {
      errors.push({ field: 'bid2Description', message: 'Bid 2 description is required when competitive bids were obtained' });
    }
    if (!data.bid2Total?.trim()) {
      errors.push({ field: 'bid2Total', message: 'Bid 2 total is required when competitive bids were obtained' });
    }
  }

  // Box 4 (selectionReason) is always required
  if (!data.selectionReason?.trim()) {
    errors.push({ field: 'selectionReason', message: 'Vendor selection reason is required' });
  }

  return errors;
}

export interface FormValidationErrors {
  basicDetails: ValidationError[];
  thirdPartyRisk: ValidationError[];
  commercialDueDiligence: ValidationError[];
  bids: ValidationError[];
}

/**
 * Get all validation errors for the entire form
 */
export function validateAllForms(
  basicDetails: BasicDetailsData,
  thirdPartyRisk: ThirdPartyRiskData,
  commercialDueDiligence: CommercialDueDiligenceData,
  bids: BidsData
): FormValidationErrors {
  return {
    basicDetails: validateBasicDetails(basicDetails),
    thirdPartyRisk: validateThirdPartyRisk(thirdPartyRisk),
    commercialDueDiligence: validateCommercialDueDiligence(
      commercialDueDiligence,
      basicDetails.estimatedContractAmount
    ),
    bids: validateBids(bids, basicDetails.competitiveBidsObtained),
  };
}

/**
 * Check if there are any validation errors across all forms
 */
export function hasValidationErrors(validationResults: FormValidationErrors): boolean {
  return Object.values(validationResults).some(errors => errors.length > 0);
}

/**
 * Get a summary of all validation errors
 */
export function getValidationSummary(validationResults: FormValidationErrors): string {
  const messages: string[] = [];
  
  if (validationResults.basicDetails?.length > 0) {
    messages.push(`Form A: ${validationResults.basicDetails.length} error(s)`);
  }
  if (validationResults.thirdPartyRisk?.length > 0) {
    messages.push(`Form B: ${validationResults.thirdPartyRisk.length} error(s)`);
  }
  if (validationResults.commercialDueDiligence?.length > 0) {
    messages.push(`Form C: ${validationResults.commercialDueDiligence.length} error(s)`);
  }
  if (validationResults.bids?.length > 0) {
    messages.push(`Form D: ${validationResults.bids.length} error(s)`);
  }

  return messages.join(', ');
}
